import { InfiniteData, useQueryClient } from '@tanstack/react-query';
import { FeatureTypeEnum } from 'api/enums';
import { ClientFeatureAnnouncementList, IFeature, IFeatureAnnouncement } from 'api/models';
import { getFeatureAnnouncements, getLatestFeatureAnnouncement, markFeatureAnnouncementSeen } from 'api/services';
import { Button } from 'library/buttons';
import { Drawer } from 'library/drawer';
import { ArrowRightIcon, CrossIcon } from 'library/icons';
import useDataQuery from 'modules/shared/hooks/useDataQuery';
import useAuthSelector from 'modules/shared/hooks/useAuthSelector';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ScrollLock from 'react-scrolllock';
import {
    getFeatureCoverFile,
    getFeatureDetailsUrl,
    hasFeatureGuideline,
    getFeatureTypeLabel,
    sortFeaturesByDate,
} from '../feature-announcement.utils';
import FeatureMedia from './FeatureMedia';
import { isAnnouncementSnoozed, snoozeAnnouncement } from '../announcement-snooze';

const tagClass: Record<FeatureTypeEnum, string> = {
    [FeatureTypeEnum.NEW]: 'bg-primary text-white',
    [FeatureTypeEnum.IMPROVED]: 'bg-success-100 text-success-600',
    [FeatureTypeEnum.FIXED]: 'bg-warning-100 text-warning-600',
};

const releaseLabel = (announcement: IFeatureAnnouncement, features: IFeature[]) => {
    if (announcement.title) return announcement.title;

    const date = features[0]?.date ?? announcement.createdAt;
    return `${new Intl.DateTimeFormat('en', { month: 'long', year: 'numeric' }).format(new Date(date))} release`;
};

const ReleaseAnnouncementExperience = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { user } = useAuthSelector();
    const userId = user?._id;
    const latest = useDataQuery(['featureAnnouncements', 'latest'], getLatestFeatureAnnouncement, {
        staleTime: Infinity,
    });
    const archive = useDataQuery(
        ['featureAnnouncements', 'drawer'],
        () => getFeatureAnnouncements({ offset: 0, limit: 1 }),
        { staleTime: 10 * 60 * 1000 }
    );
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [confirmationOpen, setConfirmationOpen] = useState(false);
    const [pendingFeatureUrl, setPendingFeatureUrl] = useState<string | null>(null);
    const [index, setIndex] = useState(0);
    const [manual, setManual] = useState(false);
    const seenRequests = useRef(new Set<string>());
    const dismissedAnnouncements = useRef(new Set<string>());
    const announcement = latest.data;
    const dismissalKey = `${userId}:${announcement?._id}`;
    const features = useMemo(() => sortFeaturesByDate(announcement?.features ?? []), [announcement]);
    const current = features[index];
    const carouselRef = useRef<HTMLDivElement>(null);
    const carouselVisible = Boolean(modalOpen && announcement && current);
    const drawerAnnouncement = archive.data?.items[0];
    const drawerFeatures = useMemo(() => sortFeaturesByDate(drawerAnnouncement?.features ?? []), [drawerAnnouncement]);

    useEffect(() => {
        if (
            latest.isSuccess &&
            announcement &&
            userId &&
            features.length &&
            !dismissedAnnouncements.current.has(dismissalKey) &&
            !isAnnouncementSnoozed(userId, announcement._id)
        ) {
            setIndex(0);
            setManual(false);
            setModalOpen(true);
        }
    }, [announcement, dismissalKey, features.length, latest.isSuccess, userId]);

    useEffect(() => {
        const open = () => setDrawerOpen(true);
        window.addEventListener('open-release-announcements', open);
        return () => window.removeEventListener('open-release-announcements', open);
    }, []);

    const acknowledge = useCallback(() => {
        if (!announcement || seenRequests.current.has(announcement._id)) return;

        seenRequests.current.add(announcement._id);
        dismissedAnnouncements.current.add(dismissalKey);
        latest.setData(null);
        queryClient.setQueryData<ClientFeatureAnnouncementList>(['featureAnnouncements', 'drawer'], (data) =>
            data
                ? {
                      ...data,
                      items: data.items.map((item) =>
                          item._id === announcement._id ? { ...item, isSeen: true } : item
                      ),
                  }
                : data
        );
        queryClient.setQueryData<InfiniteData<ClientFeatureAnnouncementList>>(
            ['featureAnnouncements', 'archive'],
            (data) =>
                data
                    ? {
                          ...data,
                          pages: data.pages.map((page) => ({
                              ...page,
                              items: page.items.map((item) =>
                                  item._id === announcement._id ? { ...item, isSeen: true } : item
                              ),
                          })),
                      }
                    : data
        );
        void markFeatureAnnouncementSeen(announcement._id).catch(() => {
            seenRequests.current.delete(announcement._id);
            latest.setData((cachedAnnouncement) => cachedAnnouncement ?? announcement);
            queryClient.invalidateQueries(['featureAnnouncements']);
        });
    }, [announcement, dismissalKey, latest, queryClient]);

    const closeModal = useCallback(() => {
        setPendingFeatureUrl(null);
        setModalOpen(false);
        setConfirmationOpen(true);
    }, []);

    const cancelConfirmation = useCallback(() => {
        setConfirmationOpen(false);
        setPendingFeatureUrl(null);
        setModalOpen(true);
    }, []);

    const finishAnnouncement = (markSeen: boolean) => {
        if (markSeen) acknowledge();
        else if (announcement) {
            dismissedAnnouncements.current.add(dismissalKey);
            if (userId) snoozeAnnouncement(userId, announcement._id);
        }
        setConfirmationOpen(false);
        setModalOpen(false);
        setPendingFeatureUrl(null);
        if (pendingFeatureUrl) navigate(pendingFeatureUrl);
    };

    const move = useCallback(
        (amount: number) => {
            setManual(true);
            setIndex((value) => Math.min(Math.max(value + amount, 0), features.length - 1));
        },
        [features.length]
    );

    useEffect(() => {
        if (
            !modalOpen ||
            manual ||
            index >= features.length - 1 ||
            window.matchMedia('(prefers-reduced-motion: reduce)').matches
        )
            return;

        const timer = window.setTimeout(() => setIndex((value) => value + 1), 8000);
        return () => window.clearTimeout(timer);
    }, [modalOpen, manual, index, features.length]);

    useEffect(() => {
        const keydown = (event: KeyboardEvent) => {
            if (confirmationOpen) {
                if (event.key === 'Escape') cancelConfirmation();
                return;
            }
            if (!modalOpen) return;
            if (event.key === 'Escape') closeModal();
            if (event.key === 'ArrowRight') move(1);
            if (event.key === 'ArrowLeft') move(-1);
        };

        window.addEventListener('keydown', keydown);
        return () => window.removeEventListener('keydown', keydown);
    }, [cancelConfirmation, closeModal, confirmationOpen, modalOpen, move]);

    useEffect(() => {
        const carousel = carouselRef.current;
        if (!carouselVisible || !carousel) return;

        let wheelDistance = 0;
        let lastWheelTime = 0;
        let lastMoveTime = -Infinity;
        let touchStart: { x: number; y: number } | null = null;

        const wheel = (event: WheelEvent) => {
            if (event.ctrlKey) return;
            event.preventDefault();
            const now = performance.now();
            const delta =
                event.deltaY * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? carousel.clientHeight : 1);
            if (now - lastWheelTime > 180 || Math.sign(delta) !== Math.sign(wheelDistance)) wheelDistance = 0;
            lastWheelTime = now;
            if (now - lastMoveTime < 700) return;
            wheelDistance += delta;
            if (Math.abs(wheelDistance) < 50) return;
            move(wheelDistance > 0 ? 1 : -1);
            wheelDistance = 0;
            lastMoveTime = now;
        };
        const touchstart = (event: TouchEvent) => {
            touchStart =
                event.touches.length === 1 ? { x: event.touches[0].clientX, y: event.touches[0].clientY } : null;
        };
        const touchend = (event: TouchEvent) => {
            if (!touchStart || !event.changedTouches.length) return;
            const deltaX = touchStart.x - event.changedTouches[0].clientX;
            const deltaY = touchStart.y - event.changedTouches[0].clientY;
            touchStart = null;
            if (Math.abs(deltaY) >= 50 && Math.abs(deltaY) > Math.abs(deltaX)) move(deltaY > 0 ? 1 : -1);
        };
        const touchcancel = () => {
            touchStart = null;
        };

        carousel.addEventListener('wheel', wheel, { passive: false });
        carousel.addEventListener('touchstart', touchstart, { passive: true });
        carousel.addEventListener('touchend', touchend, { passive: true });
        carousel.addEventListener('touchcancel', touchcancel);
        return () => {
            carousel.removeEventListener('wheel', wheel);
            carousel.removeEventListener('touchstart', touchstart);
            carousel.removeEventListener('touchend', touchend);
            carousel.removeEventListener('touchcancel', touchcancel);
        };
    }, [carouselVisible, move]);

    const openFeature = (feature: IFeature, fromPopup = false) => {
        if (fromPopup) {
            setPendingFeatureUrl(getFeatureDetailsUrl(feature));
            setModalOpen(false);
            setConfirmationOpen(true);
            return;
        }
        setModalOpen(false);
        setDrawerOpen(false);

        navigate(getFeatureDetailsUrl(feature));
    };

    return (
        <>
            {modalOpen && announcement && current && (
                <div
                    className="fixed inset-0 z-[100] grid place-items-center bg-black/55 p-4 backdrop-blur-[3px]"
                    role="dialog"
                    aria-modal="true"
                    aria-label="Product updates"
                >
                    <ScrollLock />
                    <div
                        ref={carouselRef}
                        className="relative flex h-[min(620px,calc(100vh-32px))] w-full max-w-[1080px] overflow-hidden rounded-2xl bg-primary text-white shadow-[0_32px_80px_rgba(0,29,71,0.45)]"
                        style={{ fontFamily: "'Poppins', sans-serif" }}
                    >
                        <div className="pointer-events-none absolute inset-0 opacity-30 [background-image:repeating-linear-gradient(135deg,rgba(255,255,255,.38)_0_1px,transparent_1px_26px)] [mask-image:radial-gradient(circle_at_100%_0,#000,transparent_58%)]" />
                        <div className="pointer-events-none absolute inset-0 opacity-30 [background-image:repeating-linear-gradient(135deg,rgba(255,255,255,.38)_0_1px,transparent_1px_26px)] [mask-image:radial-gradient(circle_at_0_100%,#000,transparent_58%)]" />
                        <header className="absolute left-0 right-0 top-0 z-10 flex items-center justify-between p-7 md:px-9">
                            <div className="flex items-center gap-3">
                                <div className="grid h-12 w-12 place-items-center rounded-xl bg-white shadow-lg">
                                    <img
                                        src="/logos/logo.svg"
                                        alt="ProgressCenter"
                                        className="h-7 max-w-8 object-contain"
                                    />
                                </div>
                                <h2 className="text-3xl font-bold tracking-tight">
                                    What’s <span className="text-warning-300">new</span>
                                </h2>
                                <span className="hidden items-center gap-1.5 rounded-full bg-white/15 px-3 py-2 text-xs font-bold uppercase tracking-wider md:flex">
                                    <span className="text-warning-300">•</span>
                                    <span>{releaseLabel(announcement, features)}</span>
                                </span>
                            </div>
                            <button
                                type="button"
                                onClick={closeModal}
                                className="rounded-full bg-white/15 p-3 hover:bg-white/25"
                                aria-label="Close"
                            >
                                <CrossIcon className="h-3 w-3" />
                            </button>
                        </header>
                        <section className="relative z-10 flex w-[440px] max-w-[58%] flex-col justify-center px-8 pt-20 md:px-9">
                            <div className="flex items-center gap-3">
                                <span className="font-bold tracking-wider text-warning-300">
                                    {String(index + 1).padStart(2, '0')} / {String(features.length).padStart(2, '0')}
                                </span>
                                <span className="rounded-full bg-white px-3 py-1 text-xs font-bold uppercase tracking-wide text-grayscale-800">
                                    {getFeatureTypeLabel(current.type)}
                                </span>
                                <span className="text-sm text-white/65">{current.module}</span>
                            </div>
                            <h3 className="mt-5 text-3xl font-bold leading-[1.1] tracking-tight md:text-[40px]">
                                {current.title}
                            </h3>
                            <p className="mt-5 max-w-md text-base leading-relaxed text-white/80">
                                {current.description}
                            </p>
                            <div className="mt-7 flex flex-wrap gap-3">
                                {hasFeatureGuideline(current) && (
                                    <Button color="secondary" size="normal" onClick={() => openFeature(current, true)}>
                                        Know more <ArrowRightIcon className="h-3 w-3" />
                                    </Button>
                                )}
                                <Button
                                    color="secondary"
                                    variant="shaded"
                                    size="normal"
                                    className="border border-white/50 bg-transparent text-white hover:bg-white/10"
                                    onClick={() => (index === features.length - 1 ? closeModal() : move(1))}
                                >
                                    {index === features.length - 1 ? 'Done' : 'Next'}
                                </Button>
                            </div>
                        </section>
                        {(getFeatureCoverFile(current) || current.coverImageUrl) && (
                            <div
                                className="absolute bottom-[-72px] left-[45%] right-[-130px] hidden max-w-[680px] md:block"
                                style={{ perspective: '6000px', perspectiveOrigin: 'left bottom' }}
                            >
                                <div
                                    className="overflow-hidden rounded-[14px] bg-white shadow-[-32px_36px_72px_rgba(0,29,71,.6)]"
                                    style={{
                                        transform: 'rotateY(-24deg) rotateX(8deg) translateZ(-8px)',
                                        transformOrigin: 'left bottom',
                                        transformStyle: 'preserve-3d',
                                    }}
                                >
                                    <div className="flex h-7 items-center gap-1.5 border-b border-grayscale-200 px-3">
                                        <i className="h-2 w-2 rounded-full bg-danger-500" />
                                        <i className="h-2 w-2 rounded-full bg-warning-500" />
                                        <i className="h-2 w-2 rounded-full bg-success-500" />
                                    </div>
                                    <FeatureMedia
                                        feature={current}
                                        controls={false}
                                        className="aspect-[16/10] w-full object-cover object-left-top"
                                    />
                                </div>
                            </div>
                        )}
                        <nav className="absolute right-5 top-1/2 z-20 flex -translate-y-1/2 flex-col gap-2 rounded-full bg-black/25 px-2 py-3 backdrop-blur">
                            {features.map((feature, featureIndex) => (
                                <button
                                    key={feature._id}
                                    aria-label={`Show ${feature.title}`}
                                    onClick={() => {
                                        setManual(true);
                                        setIndex(featureIndex);
                                    }}
                                    className={`h-4 w-2 overflow-hidden rounded-full ${featureIndex === index ? 'bg-white/35' : 'bg-white/40'}`}
                                >
                                    <span
                                        className={`block w-full bg-white transition-all ${featureIndex <= index ? 'h-full' : 'h-0'}`}
                                    />
                                </button>
                            ))}
                        </nav>
                    </div>
                </div>
            )}

            <Drawer
                show={confirmationOpen}
                onClose={cancelConfirmation}
                role="dialog"
                aria-modal="true"
                aria-labelledby="announcement-confirmation-title"
            >
                <h4 id="announcement-confirmation-title" className="pr-8">
                    Show this announcement again?
                </h4>
                <p className="mt-4 text-sm leading-relaxed text-grayscale-500">
                    Choose “Remind me tomorrow” to hide this announcement for 24 hours, or “Don’t show again” to stop
                    showing it automatically. These features remain available in What’s New.
                </p>
                <div className="mt-6 flex flex-col gap-3">
                    <Button color="primary" onClick={() => finishAnnouncement(true)}>
                        Don’t show again
                    </Button>
                    <Button color="secondary" onClick={() => finishAnnouncement(false)}>
                        Remind me tomorrow
                    </Button>
                </div>
            </Drawer>

            <Drawer
                show={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                className="!w-full !p-0 md:!w-[440px]"
                style={{ fontFamily: "'Poppins', sans-serif" }}
            >
                <div className="flex h-full flex-col">
                    <div className="border-b border-separator-color-light p-6">
                        <h4>What’s New</h4>
                        <p className="mt-1">Features and updates released to ProgressCenter.</p>
                    </div>
                    <div className="flex-1 space-y-6 overflow-y-auto p-6">
                        {archive.isLoading && <div className="h-48 animate-pulse rounded-xl bg-grayscale-100" />}
                        {archive.isError && <p className="text-sm text-danger-500">Unable to load product updates.</p>}
                        {drawerAnnouncement && (
                            <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-wider text-grayscale-500">
                                <span>{releaseLabel(drawerAnnouncement, drawerFeatures)}</span>
                                <span className="text-primary">{drawerFeatures.length} updates</span>
                            </div>
                        )}
                        {drawerFeatures.map((feature) => {
                            const linkClassName =
                                'mt-3 inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline';

                            return (
                                <article key={feature._id} className="pb-6 last:pb-0">
                                    {(getFeatureCoverFile(feature) || feature.coverImageUrl) && (
                                        <FeatureMedia
                                            feature={feature}
                                            className="h-[170px] w-full rounded-xl object-cover"
                                        />
                                    )}
                                    <div className="mt-3 flex items-center gap-2">
                                        <span
                                            className={`rounded-full px-2 py-1 text-xs font-semibold ${tagClass[feature.type]}`}
                                        >
                                            {getFeatureTypeLabel(feature.type)}
                                        </span>
                                        <span className="text-xs text-grayscale-500">{feature.module}</span>
                                    </div>
                                    <h6 className="mt-3">{feature.title}</h6>
                                    <p className="mt-2 leading-relaxed">{feature.description}</p>
                                    {hasFeatureGuideline(feature) && (
                                        <Link
                                            to={getFeatureDetailsUrl(feature)}
                                            className={linkClassName}
                                            onClick={() => setDrawerOpen(false)}
                                        >
                                            Know more <span aria-hidden="true">&gt;</span>
                                        </Link>
                                    )}
                                </article>
                            );
                        })}
                        {!archive.isLoading && !archive.isError && !drawerFeatures.length && (
                            <p className="text-sm text-grayscale-500">No product updates are available.</p>
                        )}
                    </div>
                    <div className="border-t border-separator-color-light p-5">
                        <button
                            onClick={() => {
                                setDrawerOpen(false);
                                navigate('/whats-new');
                            }}
                            className="w-full rounded-lg border border-separator-color-light px-4 py-2.5 text-sm font-semibold"
                        >
                            View all updates
                        </button>
                    </div>
                </div>
            </Drawer>
        </>
    );
};

export default ReleaseAnnouncementExperience;
